import React, { useCallback, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ArrowLeft, History, MessageSquarePlus, Send, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { chat, getRecentSessions } from '@/services/generatedAPIs';
import { SessionInfo, MessageInfo } from '@/services/generatedAPIs.schemas';
import { cn } from '@/lib/utils';

// Generate unique ID using native crypto API
const generateId = (): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback for older browsers
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

const THEME_PRIMARY = "#8A9A5B";
const THEME_WARNING = '#EC942CCC';
const THEME_ERROR = '#B85450D9';

type MessageType = 'result' | 'clarification' | 'error';

interface LocalMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    createdAt: Date;
    messageType?: MessageType; // Only set for assistant messages
}

interface InsightsChatProps {
    className?: string;
}

const EXAMPLE_QUESTIONS = [
    'teacher.insightsQuestion1',
    'teacher.insightsQuestion2',
    'teacher.insightsQuestion3',
];

// Message bubble component
const MessageBubble: React.FC<{
    message: LocalMessage;
    isUser: boolean;
}> = ({ message, isUser }) => {
    const formattedTime = format(message.createdAt, 'MMM dd, HH:mm');

    // Determine assistant message styling based on type
    const getAssistantStyle = (): { style?: React.CSSProperties; className: string } => {
        switch (message.messageType) {
            case 'clarification':
                return { style: { backgroundColor: THEME_WARNING }, className: 'text-white' };
            case 'error':
                return { style: { backgroundColor: THEME_ERROR }, className: 'text-white' };
            default:
                return { className: 'border border-gray-200 bg-white text-gray-900' };
        }
    };

    const assistantStyling = !isUser ? getAssistantStyle() : null;

    return (
        <div className={cn('flex flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
            <div
                className={cn(
                    'max-w-[85%] rounded-2xl px-4 py-2.5 text-sm',
                    isUser ? 'rounded-br-md text-white' : cn('rounded-bl-md', assistantStyling?.className)
                )}
                style={isUser ? { backgroundColor: THEME_PRIMARY } : assistantStyling?.style}
            >
                {isUser ? (
                    <p className="whitespace-pre-wrap break-words">{message.content}</p>
                ) : (
                    <MarkdownRenderer>{message.content}</MarkdownRenderer>
                )}
            </div>
            <span className="px-1 text-[10px] text-gray-400">{formattedTime}</span>
        </div>
    );
};

// Session item component for history
const SessionItem: React.FC<{
    session: SessionInfo;
    onSelect: (session: SessionInfo) => void;
}> = ({ session, onSelect }) => {
    const { t } = useTranslation();
    const firstMessage = session.messages?.[0];
    const preview = firstMessage?.content?.slice(0, 50) || t('teacher.noMessages');
    const lastMessageTime = session.lastMessageAt
        ? format(new Date(session.lastMessageAt), 'MMM dd, HH:mm')
        : '';

    return (
        <button
            onClick={() => onSelect(session)}
            className="w-full rounded-lg border border-gray-200 bg-white p-3 text-left transition-colors hover:bg-gray-50"
        >
            <p className="truncate text-sm font-medium text-gray-900">
                {preview}
                {firstMessage?.content && firstMessage.content.length > 50 ? '...' : ''}
            </p>
            <p className="mt-1 text-xs text-gray-500">{lastMessageTime}</p>
        </button>
    );
};

export const InsightsChat: React.FC<InsightsChatProps> = ({ className }) => {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<LocalMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState<string>(() => generateId());
    const [showHistory, setShowHistory] = useState(false);
    const [sessions, setSessions] = useState<SessionInfo[]>([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Scroll to bottom when messages change
    useEffect(() => {
        if (scrollAreaRef.current) {
            const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
            if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
        }
    }, [messages]);

    // Start a new session
    const handleNewSession = useCallback(() => {
        setSessionId(generateId());
        setMessages([]);
        setShowHistory(false);
        inputRef.current?.focus();
    }, []);

    // Load session history
    const handleShowHistory = useCallback(async () => {
        if (showHistory) {
            setShowHistory(false);
            return;
        }

        setIsLoadingSessions(true);
        try {
            const response = await getRecentSessions();
            if (response.code === 200 && response.data?.sessions) {
                setSessions(response.data.sessions);
            }
        } catch (error) {
            console.error('Failed to load sessions:', error);
        } finally {
            setIsLoadingSessions(false);
            setShowHistory(true);
        }
    }, [showHistory]);

    // Select a session from history
    const handleSelectSession = useCallback((session: SessionInfo) => {
        setSessionId(session.sessionId || generateId());
        setMessages(
            (session.messages || []).map((msg: MessageInfo, index: number) => ({
                id: `${session.sessionId}-${index}`,
                role: (msg.role as 'user' | 'assistant') || 'assistant',
                content: msg.content || '',
                messageType: msg.type as MessageType | undefined,
                createdAt: new Date(msg.createdAt || Date.now()),
            }))
        );
        setShowHistory(false);
    }, []);

    // Send a message
    const handleSendMessage = useCallback(
        async (content: string) => {
            if (!content.trim() || isLoading) return;

            const userMessage: LocalMessage = {
                id: generateId(),
                role: 'user',
                content: content.trim(),
                createdAt: new Date(),
            };

            setMessages((prev) => [...prev, userMessage]);
            setInputValue('');
            setIsLoading(true);

            try {
                const response = await chat({ message: content.trim(), sessionId });

                let assistantContent = '';
                let messageType: MessageType = 'result';
                if (response.code === 200 && response.data) {
                    messageType = (response.data.type as MessageType) || 'result';
                    assistantContent =
                        response.data.summary ||
                        response.data.message ||
                        t('teacher.insightsNoResponse');
                } else {
                    messageType = 'error';
                    assistantContent = t('teacher.insightsError');
                }

                const assistantMessage: LocalMessage = {
                    id: generateId(),
                    role: 'assistant',
                    content: assistantContent,
                    messageType,
                    createdAt: new Date(),
                };

                setMessages((prev) => [...prev, assistantMessage]);
            } catch (error) {
                console.error('Failed to send message:', error);
                const errorMessage: LocalMessage = {
                    id: generateId(),
                    role: 'assistant',
                    content: t('teacher.insightsError'),
                    messageType: 'error',
                    createdAt: new Date(),
                };
                setMessages((prev) => [...prev, errorMessage]);
            } finally {
                setIsLoading(false);
            }
        },
        [isLoading, sessionId, t]
    );

    // Handle form submit
    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            handleSendMessage(inputValue);
        },
        [handleSendMessage, inputValue]
    );

    // Handle example question click
    const handleExampleClick = useCallback(
        (questionKey: string) => {
            const question = t(questionKey);
            handleSendMessage(question);
        },
        [handleSendMessage, t]
    );

    // Handle Enter key
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(inputValue);
            }
        },
        [handleSendMessage, inputValue]
    );

    const showExampleQuestions = messages.length === 0 && !showHistory;

    return (
        <div className={cn('flex flex-col', className)} style={{ height: '460px' }}>
            {/* Header buttons */}
            <div className="mb-3 flex gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNewSession}
                    className="flex items-center gap-1.5 text-xs cursor-pointer"
                >
                    <MessageSquarePlus className="h-3.5 w-3.5" />
                    {t('teacher.newConversation')}
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShowHistory}
                    className="flex items-center gap-1.5 text-xs cursor-pointer"
                    disabled={isLoadingSessions}
                >
                    {isLoadingSessions ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : showHistory ? (
                        <ArrowLeft className="h-3.5 w-3.5" />
                    ) : (
                        <History className="h-3.5 w-3.5" />
                    )}
                    {showHistory ? t('teacher.back') : t('teacher.recentSessions')}
                </Button>
            </div>

            {/* Main content area */}
            <div className="flex-1 overflow-y-auto">
                {showHistory ? (
                    // Session history view
                    <ScrollArea className="h-full pr-2">
                        <div className="space-y-2">
                            {sessions.length > 0 ? (
                                sessions.map((session) => (
                                    <SessionItem
                                        key={session.sessionId}
                                        session={session}
                                        onSelect={handleSelectSession}
                                    />
                                ))
                            ) : (
                                <p className="py-4 text-center text-sm text-gray-500">
                                    {t('teacher.noRecentSessions')}
                                </p>
                            )}
                            <p className="text-xs text-gray-400 text-right">
                                {t('teacher.historyNote')}
                            </p>
                        </div>
                    </ScrollArea>
                ) : showExampleQuestions ? (
                    // Example questions view
                    <div className="flex h-full flex-col items-center justify-center px-2">
                        <p className="mb-4 text-center text-sm text-gray-600">
                            {t('teacher.insightsWelcome')}
                        </p>
                        <div className="flex w-full flex-col gap-2">
                            {EXAMPLE_QUESTIONS.map((questionKey) => (
                                <button
                                    key={questionKey}
                                    onClick={() => handleExampleClick(questionKey)}
                                    className="rounded-lg border border-gray-200 bg-background px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:border-gray-300 hover:bg-muted"
                                >
                                    {t(questionKey)}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    // Chat messages view
                    <ScrollArea ref={scrollAreaRef} className="h-full pr-2">
                        <div className="flex flex-col gap-3 pb-2">
                            {messages.map((message) => (
                                <MessageBubble
                                    key={message.id}
                                    message={message}
                                    isUser={message.role === 'user'}
                                />
                            ))}
                            {isLoading && (
                                <div className="flex items-start">
                                    <div className="rounded-2xl rounded-bl-md border border-gray-200 bg-muted px-4 py-2.5">
                                        <p className="animate-pulse text-sm text-muted-foreground">
                                            {t('teacher.insightsLoading')}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                )}
            </div>

            {/* Input area */}
            {!showHistory && (
                <form onSubmit={handleSubmit} className="mt-3">
                    <div className="flex items-center gap-2">
                        <textarea
                            ref={inputRef}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={t('teacher.insightsPlaceholder')}
                            className="flex-1 resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm placeholder:text-gray-400 focus:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-300 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                            rows={2}
                            disabled={isLoading}
                        />
                        <Button
                            type="submit"
                            size="icon"
                            className="h-9 w-9 shrink-0 rounded-full cursor-pointer"
                            disabled={!inputValue.trim() || isLoading}
                            style={{
                                backgroundColor: inputValue.trim() ? THEME_PRIMARY : undefined,
                            }}
                        >
                            <Send className="h-4 w-4" />
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default InsightsChat;
